import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { User, Mail, Phone, Briefcase, Lock, Check, X, ArrowRight } from 'lucide-react';

const InputField = ({ icon: Icon, label, name, type = "text", placeholder, value, onChange, onBlur, error, touched }) => (
    <div className="space-y-1.5">
        <label className="text-xs font-medium text-slate-400 ml-1 uppercase tracking-wider">{label}</label>
        <div className="relative group">
            <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-primary transition-colors" />
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                className={`w-full bg-background/50 border rounded-xl py-3 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 transition-all ${touched && error
                    ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                    : (touched && !error && value)
                        ? 'border-green-500/50 focus:border-green-500 focus:ring-green-500/20'
                        : 'border-white/10 focus:border-primary/50 focus:ring-primary/50'
                    }`}
                placeholder={placeholder}
                required
            />
            {touched && !error && value && (
                <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
            )}
        </div>
        {touched && error && (
            <p className="text-xs text-red-400 ml-1">{error}</p>
        )}
    </div>
);

const Signup = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone_number: '',
        profession: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [rules, setRules] = useState({
        length: false,
        upper: false,
        lower: false,
        number: false,
        special: false
    });
    const [touched, setTouched] = useState({});

    const { login, user } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validatePhone = (phone) => /^\d{10}$/.test(phone);

    useEffect(() => {
        const pwd = formData.password;
        setRules({
            length: pwd.length >= 8,
            upper: /[A-Z]/.test(pwd),
            lower: /[a-z]/.test(pwd),
            number: /\d/.test(pwd),
            special: /[@$!%*?&]/.test(pwd)
        });
    }, [formData.password]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === 'email') {
            setErrors(prev => ({ ...prev, email: validateEmail(value) ? '' : 'Invalid email format' }));
        }
        if (name === 'phone_number') {
            setErrors(prev => ({ ...prev, phone_number: validatePhone(value) ? '' : 'Must be exactly 10 digits' }));
        }
    };

    const handleBlur = (e) => {
        setTouched({ ...touched, [e.target.name]: true });
    };

    const isFormValid = () => {
        return formData.name &&
            validateEmail(formData.email) &&
            validatePhone(formData.phone_number) &&
            formData.profession &&
            Object.values(rules).every(Boolean);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) return;

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/auth/signup`, {
                user: formData
            });

            // login(response.data.user, response.data.token); // remove auto-login
            navigate('/verify-otp', { state: { email: formData.email } });
        } catch (err) {
            const errorMsg = err.response?.data?.errors?.join(', ') || 'Signup failed';
            setErrors({ submit: errorMsg });
        }
    };

    return (
        <div className="min-h-screen pt-20 pb-10 flex items-center justify-center relative overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-background">
                <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-primary/10 rounded-full blur-[150px] animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-[60%] h-[60%] bg-secondary/5 rounded-full blur-[150px] animate-float"></div>
            </div>

            <div className="relative w-full max-w-2xl mx-4">
                <div className="bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-bold font-display text-white mb-2">{t('auth.signup_title')}</h2>
                        <p className="text-slate-400">{t('auth.signup_subtitle')}</p>
                    </div>

                    {errors.submit && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-8 flex items-center gap-2">
                            <X className="w-5 h-5" />
                            {errors.submit}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField
                                icon={User}
                                label={t('auth.name_label')}
                                name="name"
                                placeholder="John Doe"
                                value={formData.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.name}
                                error={errors.name} // Though currently Name has no specific error validation logic beyond required
                            />
                            <InputField
                                icon={Briefcase}
                                label={t('auth.profession_label')}
                                name="profession"
                                placeholder="Software Engineer"
                                value={formData.profession}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.profession}
                                error={errors.profession}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField
                                icon={Mail}
                                label={t('auth.email_label')}
                                name="email"
                                type="email"
                                placeholder="john@example.com"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.email}
                                error={errors.email}
                            />
                            <InputField
                                icon={Phone}
                                label={t('auth.phone_label')}
                                name="phone_number"
                                placeholder="9876543210"
                                value={formData.phone_number}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.phone_number}
                                error={errors.phone_number}
                            />
                        </div>

                        <div className="space-y-4 pt-2">
                            <InputField
                                icon={Lock}
                                label={t('auth.password_label')}
                                name="password"
                                type="password"
                                placeholder="Create a secure password"
                                value={formData.password}
                                onChange={handleChange}
                                onBlur={handleBlur} // Password acts slightly differently with rules visual
                                touched={touched.password}
                                error={null}
                            />

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.entries({
                                    length: '8+ chars',
                                    upper: 'Uppercase',
                                    lower: 'Lowercase',
                                    number: 'Number',
                                    special: 'Symbol'
                                }).map(([key, label]) => (
                                    <div key={key} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border transition-colors ${rules[key]
                                        ? 'bg-green-500/10 border-green-500/20 text-green-400'
                                        : 'bg-background/30 border-white/5 text-slate-500'
                                        }`}>
                                        <div className={`w-1.5 h-1.5 rounded-full ${rules[key] ? 'bg-green-500' : 'bg-slate-600'}`}></div>
                                        {label}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={!isFormValid()}
                                className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold py-4 rounded-xl shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
                            >
                                {t('auth.submit_signup')}
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-slate-400 text-sm">
                            {t('auth.have_account')}{' '}
                            <Link to="/login" className="text-white font-medium hover:text-primary transition-colors">
                                {t('nav.login')}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup;
